import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import Avatar from "../Avatar";
import { AvatarDirection, AvatarPose, AvatarState } from "../../types/avatar";
import { useAuth } from "../AuthContext";
import { collection, onSnapshot, doc, updateDoc } from "firebase/firestore";
import { db, updateVirtualAvatar, deleteVirtualAvatar } from "../../firebase";
import { SessionSettingsResponse } from "./types";

interface SceneProps {
  roomId: string;
  meetingClient?: any | null;
  sessionSetting?: SessionSettingsResponse | null;
  onError?: (error: string) => void;
}

interface ParticipantData {
  id: string;
  name: string;
  roleInSession: "representative" | "observer";
  avatarId: string | null;
  x?: number;
  y?: number;
  xRel?: number;
  yRel?: number;
  pose?: AvatarPose;
  direction?: AvatarDirection;
  role?: "terapeuta" | "paciente" | "participante";
}

interface VirtualAvatarData {
  id: string;
  name: string;
  avatarId: string | null;
  xRel?: number;
  yRel?: number;
  pose?: AvatarPose;
  direction?: AvatarDirection;
  createdBy?: string;
  createdByName?: string | null;
  isVirtual?: boolean;
}

type SceneAvatar = AvatarState & { isVirtual: boolean };

const Scene: React.FC<SceneProps> = ({ meetingClient, roomId, sessionSetting, onError }) => {
  const { user } = useAuth();
  const sceneRef = useRef<HTMLDivElement>(null);
  const [sceneSize, setSceneSize] = useState({ width: 0, height: 0 });
  const [participants, setParticipants] = useState<ParticipantData[]>([]);
  const [virtualAvatars, setVirtualAvatars] = useState<VirtualAvatarData[]>([]);
  const [loading, setLoading] = useState(true);
  const [isInitialPositionSet, setIsInitialPositionSet] = useState(false);
  const [activeParticipantIds, setActiveParticipantIds] = useState<Set<string>>(new Set());
  const mountedRef = useRef(true);

  const [backgroundImage, setBackgroundImage] = useState("");

  // ======================= INÍCIO DA CORREÇÃO =======================
  // Efeito para determinar a imagem de fundo com base no tamanho da tela, usando a API matchMedia para maior fiabilidade.
  useEffect(() => {
    // Media query para verificar ecrãs com largura máxima de 767px (ponto de quebra para mobile)
    const mediaQuery = window.matchMedia("(max-width: 767px)");

    // Função que define a imagem de fundo com base na correspondência da media query
    const handleScreenChange = (e: MediaQueryListEvent | MediaQueryList) => {
      if (e.matches) {
        // Se a media query corresponde (tela pequena), usa a imagem mobile.
        setBackgroundImage("/assets/fundo.png");
      } else {
        // Caso contrário, usa a imagem desktop.
        setBackgroundImage("/assets/fundo2.jpg");
      }
    };

    // Adiciona um listener que será chamado quando o tamanho da tela cruzar o ponto de quebra.
    mediaQuery.addEventListener("change", handleScreenChange);

    // Chama a função uma vez no início para definir a imagem correta no carregamento da página.
    handleScreenChange(mediaQuery);

    // Função de limpeza para remover o listener quando o componente for desmontado.
    return () => {
      mediaQuery.removeEventListener("change", handleScreenChange);
    };
  }, []); // O array de dependências vazio garante que o efeito só é executado uma vez na montagem.
  // ======================== FIM DA CORREÇÃO =========================

  const debugLog = useCallback((message: string, data?: any) => {
    if (process.env.NODE_ENV === "development") {
      console.log(`🎬 Scene: ${message}`, data || "");
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const updateSceneSize = () => {
      if (sceneRef.current) {
        const rect = sceneRef.current.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          setSceneSize({ width: rect.width, height: rect.height });
        }
      }
    };

    updateSceneSize();
    const timeoutId = setTimeout(updateSceneSize, 100);

    const resizeObserver = new ResizeObserver(updateSceneSize);
    if (sceneRef.current) {
      resizeObserver.observe(sceneRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, []);

  const getSceneDimensions = useCallback(() => {
    if (sceneSize.width > 0 && sceneSize.height > 0) {
      return sceneSize;
    }
    if (sceneRef.current) {
      const rect = sceneRef.current.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        return { width: rect.width, height: rect.height };
      }
    }
    return { width: window.innerWidth, height: window.innerHeight };
  }, [sceneSize]);

  // CORREÇÃO PRINCIPAL: A lógica de cálculo agora considera a altura do cenário.
  const calculateRelativePositioning = useCallback((dimensions: { width: number; height: number }) => {
    // Determina se deve usar o tamanho de avatar mobile com base na LARGURA ou ALTURA.
    const useMobileAvatarSize = dimensions.width < 768 || dimensions.height < 600;

    const avatarWidth = useMobileAvatarSize ? 156 : 234;
    const avatarHeight = useMobileAvatarSize ? 208 : 312;

    const navigableWidth = Math.max(1, dimensions.width - avatarWidth);
    const navigableHeight = Math.max(1, dimensions.height - avatarHeight);

    return { navigableWidth, navigableHeight };
  }, []);

  const relativeToAbsolute = useCallback(
    (xRel: number, yRel: number) => {
      const dimensions = getSceneDimensions();
      const { navigableWidth, navigableHeight } = calculateRelativePositioning(dimensions);

      const x = Math.round(xRel * navigableWidth);
      const y = Math.round(yRel * navigableHeight);

      return { x, y };
    },
    [getSceneDimensions, calculateRelativePositioning]
  );

  const computeAbsolutePosition = useCallback(
    (xRel?: number, yRel?: number) => {
      if (typeof xRel !== "number" || typeof yRel !== "number") {
        return { x: 0, y: 0 };
      }
      return relativeToAbsolute(xRel, yRel);
    },
    [relativeToAbsolute]
  );

  const absoluteToRelative = useCallback(
    (x: number, y: number) => {
      const dimensions = getSceneDimensions();
      const { navigableWidth, navigableHeight } = calculateRelativePositioning(dimensions);

      const xRel = Math.max(0, Math.min(1, x / navigableWidth));
      const yRel = Math.max(0, Math.min(1, y / navigableHeight));

      return { xRel, yRel };
    },
    [getSceneDimensions, calculateRelativePositioning]
  );

  const updateParticipantData = useCallback(
    async (userId: string, dataToUpdate: object) => {
      if (!roomId || !userId) return;
      const participantDocRef = doc(db, "rooms", roomId, "participants", userId);
      try {
        await updateDoc(participantDocRef, dataToUpdate);
      } catch (err) {
        console.error(`Error updating participant ${userId}:`, err);
        if (mountedRef.current) {
          onError?.(`Erro ao sincronizar alteração.`);
        }
      }
    },
    [roomId, onError]
  );

  useEffect(() => {
    if (!roomId) return;

    setLoading(true);
    const participantsCollectionRef = collection(db, "rooms", roomId, "participants");
    const unsubscribe = onSnapshot(
      participantsCollectionRef,
      (snapshot) => {
        if (!mountedRef.current) return;

        const participantsData: ParticipantData[] = [];
        snapshot.forEach((doc) => {
          participantsData.push({ id: doc.id, ...doc.data() } as ParticipantData);
        });

        setParticipants(participantsData);
        setLoading(false);
      },
      (err) => {
        console.error("Scene Firestore listener error:", err);
        if (mountedRef.current) {
          setLoading(false);
          onError?.("Não foi possível sincronizar com a sala.");
        }
      }
    );

    return () => unsubscribe();
  }, [roomId, onError]);

  useEffect(() => {
    if (!roomId) return;

    const virtualAvatarsCollectionRef = collection(db, "rooms", roomId, "virtualAvatars");
    const unsubscribe = onSnapshot(
      virtualAvatarsCollectionRef,
      (snapshot) => {
        if (!mountedRef.current) return;

        const virtualData: VirtualAvatarData[] = [];
        snapshot.forEach((docSnapshot) => {
          virtualData.push({ id: docSnapshot.id, ...docSnapshot.data() } as VirtualAvatarData);
        });

        setVirtualAvatars(virtualData);
      },
      (err) => {
        console.error("Scene Firestore virtual avatars listener error:", err);
        if (mountedRef.current) {
          onError?.("Não foi possível sincronizar avatares adicionais.");
        }
      }
    );

    return () => unsubscribe();
  }, [roomId, onError]);

  useEffect(() => {
    if (user && participants.length > 0 && sceneSize.width > 0 && !isInitialPositionSet) {
      const currentUserData = participants.find((p) => p.id === user.uid);

      if (currentUserData && currentUserData.xRel === undefined && currentUserData.yRel === undefined) {
        debugLog(`Setting initial position for user ${user.uid}`);
        const baseXRel = 0.4 + Math.random() * 0.2;
        const baseYRel = 0.4 + Math.random() * 0.2;
        updateParticipantData(user.uid, {
          xRel: Math.max(0.05, Math.min(0.95, baseXRel)),
          yRel: Math.max(0.05, Math.min(0.95, baseYRel)),
        });
        setIsInitialPositionSet(true);
      } else if (currentUserData && (currentUserData.xRel !== undefined || currentUserData.yRel !== undefined)) {
        setIsInitialPositionSet(true);
      }
    }
  }, [user, participants, sceneSize, isInitialPositionSet, updateParticipantData, debugLog]);

  useEffect(() => {
    if (!meetingClient) {
      setActiveParticipantIds(new Set());
      return;
    }

    const extractActiveIds = () => {
      if (!meetingClient?.participants?.joined?.toArray) {
        return new Set<string>();
      }

      const activeList = meetingClient.participants.joined.toArray();
      const next = new Set<string>();
      activeList.forEach((participant: any) => {
        if (participant?.customParticipantId) {
          next.add(participant.customParticipantId);
        }
      });
      return next;
    };

    setActiveParticipantIds(extractActiveIds());

    const handleParticipantJoined = (participant: any) => {
      if (!participant?.customParticipantId) return;
      setActiveParticipantIds((prev) => {
        const next = new Set(prev);
        next.add(participant.customParticipantId);
        return next;
      });
    };

    const handleParticipantLeft = (participant: any) => {
      if (!participant?.customParticipantId) return;

      const isInJoinedList = meetingClient.participants.joined
        .toArray()
        .some((p: any) => p.customParticipantId === participant.customParticipantId);
      if (!isInJoinedList) {
        setActiveParticipantIds((prev) => {
          const next = new Set(prev);
          next.delete(participant.customParticipantId);
          return next;
        });
      }
    };

    const joinedEmitter = meetingClient?.participants?.joined;
    const activeEmitter = meetingClient?.participants?.active;

    joinedEmitter?.on?.("participantJoined", handleParticipantJoined);
    joinedEmitter?.on?.("participantLeft", handleParticipantLeft);
    activeEmitter?.on?.("participantLeft", handleParticipantLeft);
    activeEmitter?.on?.("participantDisconnected", handleParticipantLeft);

    return () => {
      joinedEmitter?.off?.("participantJoined", handleParticipantJoined);
      joinedEmitter?.off?.("participantLeft", handleParticipantLeft);
      activeEmitter?.off?.("participantLeft", handleParticipantLeft);
      activeEmitter?.off?.("participantDisconnected", handleParticipantLeft);
    };
  }, [meetingClient, roomId]);

  const virtualAvatarIds = useMemo(() => {
    return new Set(virtualAvatars.map((virtualItem) => virtualItem.id));
  }, [virtualAvatars]);

  const avatars = useMemo<SceneAvatar[]>(() => {
    if (sceneSize.width === 0 || sceneSize.height === 0) {
      return [];
    }

    const canShowParticipantAvatar = (participant: ParticipantData) => {
      const hasRepresentativeAvatar = participant.roleInSession === "representative" && !!participant.avatarId;
      if (!hasRepresentativeAvatar) {
        return false;
      }

      if (!meetingClient) {
        return false;
      }

      if (participant.id === user?.uid) {
        return true;
      }

      return activeParticipantIds.size === 0 || activeParticipantIds.has(participant.id);
    };

    const participantAvatars: SceneAvatar[] = participants
      .filter(canShowParticipantAvatar)
      .map((p) => {
        const { x: absoluteX, y: absoluteY } = computeAbsolutePosition(p.xRel, p.yRel);

        return {
          id: p.id,
          username: p.name,
          avatarId: p.avatarId!,
          pose: p.pose || "standing",
          direction: p.direction || "front",
          x: absoluteX,
          y: absoluteY,
          isCurrentUser: user?.uid === p.id,
          role: p.role,
          isVirtual: false,
        } as SceneAvatar;
      });

    const extraAvatars: SceneAvatar[] = virtualAvatars
      .filter((v) => !!v.avatarId)
      .map((v) => {
        const { x: absoluteX, y: absoluteY } = computeAbsolutePosition(v.xRel, v.yRel);

        return {
          id: v.id,
          username: v.name || "Avatar",
          avatarId: v.avatarId!,
          pose: v.pose || "standing",
          direction: v.direction || "front",
          x: absoluteX,
          y: absoluteY,
          isCurrentUser: false,
          role: "participante",
          isVirtual: true,
        } as SceneAvatar;
      });

    const sortedParticipants = [...participantAvatars].sort((a, b) => a.username.localeCompare(b.username));
    const sortedVirtuals = [...extraAvatars].sort((a, b) => a.username.localeCompare(b.username));

    return [...sortedParticipants, ...sortedVirtuals];
  }, [participants, virtualAvatars, sceneSize, user, meetingClient, activeParticipantIds, computeAbsolutePosition]);

  const canControlAvatar = useCallback(
    (avatarId: string): boolean => {
      if (!user) return false;

      if (virtualAvatarIds.has(avatarId)) {
        return sessionSetting?.isRoomModerator ?? false;
      }

      if (sessionSetting?.isRoomModerator) {
        return true;
      }

      return user.uid === avatarId;
    },
    [user, sessionSetting, virtualAvatarIds]
  );

  const lastMoveTime = useRef<number>(0);
  const handleAvatarMove = useCallback(
    async (id: string, x: number, y: number) => {
      if (!canControlAvatar(id) || sceneSize.width === 0) return;

      const now = Date.now();
      if (now - lastMoveTime.current < 50) return;
      lastMoveTime.current = now;

      const { xRel, yRel } = absoluteToRelative(x, y);
      const controlMetadata = user
        ? {
            lastControlledBy: user.uid,
            lastControlledByName: user.name || user.email || null,
          }
        : {};

      if (virtualAvatarIds.has(id)) {
        await updateVirtualAvatar(roomId, id, { xRel, yRel, ...controlMetadata });
      } else {
        await updateParticipantData(id, { xRel, yRel, ...controlMetadata });
      }
    },
    [canControlAvatar, sceneSize, absoluteToRelative, updateParticipantData, virtualAvatarIds, roomId, user]
  );

  const handleAvatarDelete = useCallback(
    async (id: string) => {
      if (!canControlAvatar(id) || !virtualAvatarIds.has(id)) {
        return;
      }

      try {
        await deleteVirtualAvatar(roomId, id);
      } catch (error) {
        console.error("Failed to delete virtual avatar", error);
        onError?.("Não foi possível remover o avatar virtual. Tente novamente.");
      }
    },
    [canControlAvatar, virtualAvatarIds, roomId, onError]
  );

  const handleAvatarPoseChange = useCallback(
    async (id: string, pose: AvatarPose) => {
      if (!canControlAvatar(id)) return;

      const controlMetadata = user
        ? {
            lastControlledBy: user.uid,
            lastControlledByName: user.name || user.email || null,
          }
        : {};

      if (virtualAvatarIds.has(id)) {
        await updateVirtualAvatar(roomId, id, { pose, ...controlMetadata });
      } else {
        await updateParticipantData(id, { pose, ...controlMetadata });
      }
    },
    [canControlAvatar, updateParticipantData, virtualAvatarIds, roomId, user]
  );

  const handleAvatarDirectionChange = useCallback(
    async (id: string, direction: AvatarDirection) => {
      if (!canControlAvatar(id)) return;

      const controlMetadata = user
        ? {
            lastControlledBy: user.uid,
            lastControlledByName: user.name || user.email || null,
          }
        : {};

      if (virtualAvatarIds.has(id)) {
        await updateVirtualAvatar(roomId, id, { direction, ...controlMetadata });
      } else {
        await updateParticipantData(id, { direction, ...controlMetadata });
      }
    },
    [canControlAvatar, updateParticipantData, virtualAvatarIds, roomId, user]
  );

  if (loading || sceneSize.width === 0 || !backgroundImage) {
    return (
      <div ref={sceneRef} className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
          <p className="text-blue-700">Carregando Cenário...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col bg-gradient-to-br from-blue-50 to-indigo-100">
      <div
        ref={sceneRef}
        className="scene-container flex-1 relative overflow-hidden"
        style={{
          // Usa a variável de estado para definir a imagem de fundo dinamicamente
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          minHeight: sceneSize.height,
        }}>

        {!!meetingClient && (
          avatars.map((avatar) => (
            <Avatar
              key={avatar.id}
              avatarId={avatar.id}
              username={avatar.username}
              avatarImageId={avatar.avatarId}
              pose={avatar.pose}
              direction={avatar.direction}
              x={avatar.x}
              y={avatar.y}
              isCurrentUser={avatar.isCurrentUser}
              canControl={canControlAvatar(avatar.id)}
              onDrag={handleAvatarMove}
              onPoseChange={handleAvatarPoseChange}
              onDirectionChange={handleAvatarDirectionChange}
              isVirtual={avatar.isVirtual}
              onDelete={handleAvatarDelete}
              sceneSize={sceneSize}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default Scene;
